import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FolderOpen, Clock, BarChart3, AlertTriangle, FileText, X, Check, Bell, ChevronRight, ChevronDown, Plus } from 'lucide-react';
import client from '../../api/client';
import { useAuth, userIsAdmin, userIsReviewer, userIsEngineer, userIsManager } from '../../context/AuthContext';
import { NAVY, AMBER, AMBER_DARK, BORDER_COLOR, bubbleStyle, navyBtn, fieldStyle } from '../../styles/tokens';

const STATUS_CONFIG = {
  draft:            { label: 'Borrador',             color: '#7c3aed', bg: '#f3e8ff' },
  submitted:        { label: 'Pendiente Ingeniero',  color: '#ca8a04', bg: '#fef9c3' },
  pending_engineer: { label: 'Pendiente Ingeniero',  color: '#2563eb', bg: '#dbeafe' },
  pending_manager:  { label: 'Pendiente Gerente',    color: '#7c3aed', bg: '#f3e8ff' },
  pending_clerk:    { label: 'Rev. Final',           color: '#0f766e', bg: '#ccfbf1' },
  approved:         { label: 'Aprobada',             color: '#16a34a', bg: '#dcfce7' },
  returned:         { label: 'Devuelto',             color: '#ea580c', bg: '#fed7aa' },
};

function fmtDate(d) {
  if (!d) return '';
  const [y, m, day] = String(d).slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, day).toLocaleDateString('es-MX', { day: '2-digit', month: 'short' });
}

export default function NominaHomePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdm = userIsAdmin(user);
  const isRev = userIsReviewer(user);
  const isEng = userIsEngineer(user);
  const isMgr = userIsManager(user);
  const canReview = isAdm || isRev || isEng || isMgr;

  const [activeTab, setActiveTab] = useState('projects');
  const [projects, setProjects] = useState([]);
  const [reviewSheets, setReviewSheets] = useState([]);
  const [recentSheets, setRecentSheets] = useState([]);
  const [globalAlerts, setGlobalAlerts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedAlert, setExpandedAlert] = useState(null);

  // Dashboard date range
  const today = new Date();
  const weekAgo = new Date(today);
  weekAgo.setDate(weekAgo.getDate() - 7);
  const [dateFrom, setDateFrom] = useState(weekAgo.toISOString().slice(0, 10));
  const [dateTo, setDateTo] = useState(today.toISOString().slice(0, 10));

  useEffect(() => {
    Promise.all([
      client.get('/api/nomina/my-projects'),
      client.get('/api/nomina/sheets', { params: { view: isAdm ? 'all' : 'project' } }),
      client.get('/api/nomina/alerts'),
    ]).then(([projRes, sheetsRes, alertsRes]) => {
      setProjects(projRes.data);
      setGlobalAlerts(alertsRes.data);
      setRecentSheets(sheetsRes.data || []);
      setReviewSheets(sheetsRes.data || []);
    }).catch(console.error).finally(() => setLoading(false));
  }, [isAdm, canReview]);

  // Collapsible state for project cards
  const [collapsedProjects, setCollapsedProjects] = useState({});
  const [collapsedStatuses, setCollapsedStatuses] = useState({});

  // Group ALL sheets by project, then by status, sorted oldest first
  const sheetsByProject = {};
  const sortedAll = [...recentSheets].sort((a, b) => new Date(a.sheet_date) - new Date(b.sheet_date));
  for (const s of sortedAll) {
    const pid = s.project_id;
    if (!sheetsByProject[pid]) sheetsByProject[pid] = {};
    if (!sheetsByProject[pid][s.status]) sheetsByProject[pid][s.status] = [];
    sheetsByProject[pid][s.status].push(s);
  }

  const STATUS_ORDER = ['returned', 'draft', 'submitted', 'pending_engineer', 'pending_manager', 'pending_clerk', 'approved'];
  const EDITABLE_STATUSES = new Set(['draft', 'returned']);
  const PROJECT_SHEET_LIMIT = 5;

  // Build a status→sheets map capped to PROJECT_SHEET_LIMIT total across statuses,
  // walking STATUS_ORDER so urgent statuses fill the cap first.
  function limitProjectSheets(projectSheets) {
    const limited = {};
    let shown = 0;
    let total = 0;
    for (const arr of Object.values(projectSheets)) total += arr.length;
    for (const st of STATUS_ORDER) {
      const arr = projectSheets[st];
      if (!arr || arr.length === 0) continue;
      const remaining = PROJECT_SHEET_LIMIT - shown;
      if (remaining <= 0) break;
      limited[st] = arr.slice(0, remaining);
      shown += limited[st].length;
    }
    return { limited, total, hiddenCount: Math.max(0, total - shown) };
  }

  // Fetch summary when dashboard tab is active or date range changes
  useEffect(() => {
    if (activeTab !== 'summary') return;
    client.get('/api/nomina/reports/summary', { params: { from: dateFrom, to: dateTo } })
      .then(r => setSummary(r.data))
      .catch(console.error);
  }, [activeTab, dateFrom, dateTo]);

  async function dismissAlert(alertId) {
    try {
      await client.post(`/api/nomina/alerts/${alertId}/dismiss`);
      setGlobalAlerts(prev => prev.filter(a => a.id !== alertId));
      setExpandedAlert(null);
    } catch (err) {
      console.error('Error dismissing alert:', err);
    }
  }

  const pendingCount = reviewSheets.filter(s =>
    s.status === 'submitted' || s.status === 'pending_engineer' || s.status === 'pending_manager'
  ).length;

  const tabs = [
    { key: 'projects', label: 'Proyectos', icon: FolderOpen, badge: pendingCount || null },
    ...(isAdm ? [{ key: 'summary', label: 'Resumen', icon: BarChart3 }] : []),
  ];

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Cargando...</div>;

  return (
    <div className="page-pad" style={{ padding: '20px 24px', maxWidth: 900, margin: '0 auto' }}>
      <h1 style={{ fontSize: 20, fontWeight: 700, color: NAVY, margin: '0 0 16px' }}>Nómina</h1>

      {/* Tabs */}
      <div className="filter-bar" style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            style={{
              padding: '6px 16px', fontSize: 12, fontWeight: 600,
              borderRadius: 20, cursor: 'pointer',
              border: `1.5px solid ${activeTab === t.key ? AMBER : BORDER_COLOR}`,
              background: activeTab === t.key ? '#fef3c7' : '#fff',
              color: activeTab === t.key ? AMBER_DARK : '#64748b',
              fontFamily: 'Inter, sans-serif',
              display: 'flex', alignItems: 'center', gap: 5,
            }}
          >
            <t.icon size={13} />
            {t.label}
            {t.badge && (
              <span style={{
                background: '#ca8a04', color: '#fff',
                borderRadius: 10, padding: '0 6px', fontSize: 10, fontWeight: 700,
              }}>
                {t.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ═══ Projects Tab — unified view with sheets grouped by status ═══ */}
      {activeTab === 'projects' && (
        <>
          {projects.length === 0 ? (
            <div style={{ ...bubbleStyle, padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>
              No hay proyectos activos.
            </div>
          ) : projects.map(p => {
            const projectSheets = sheetsByProject[p.id] || {};
            const totalSheets = Object.values(projectSheets).reduce((s, arr) => s + arr.length, 0);
            const { limited: limitedSheets, hiddenCount } = limitProjectSheets(projectSheets);
            const isProjectCollapsed = collapsedProjects[p.id];

            return (
              <div key={p.id} style={{ ...bubbleStyle, padding: 0, marginBottom: 16, overflow: 'hidden' }}>
                {/* Project header */}
                <div
                  onClick={() => setCollapsedProjects(prev => ({ ...prev, [p.id]: !prev[p.id] }))}
                  style={{
                    padding: '14px 18px', cursor: 'pointer',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    background: '#f8fafc', borderBottom: isProjectCollapsed ? 'none' : `1px solid ${BORDER_COLOR}`,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {isProjectCollapsed
                      ? <ChevronRight size={16} style={{ color: '#64748b' }} />
                      : <ChevronDown size={16} style={{ color: '#64748b' }} />
                    }
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: NAVY }}>
                        {p.code ? `${p.code} ` : ''}{p.name}
                      </div>
                      {p.client && <div style={{ fontSize: 11, color: '#94a3b8' }}>{p.client}</div>}
                    </div>
                    {p.approval_type && (
                      <span style={{
                        fontSize: 10, fontWeight: 600, padding: '1px 8px', borderRadius: 8,
                        background: p.approval_type === '3_levels' ? '#f3e8ff' : '#dbeafe',
                        color: p.approval_type === '3_levels' ? '#7c3aed' : '#2563eb',
                      }}>
                        {p.approval_type === '3_levels' ? '3 Niveles' : '2 Niveles'}
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 11, color: '#94a3b8' }}>{totalSheets} tarjeta{totalSheets !== 1 ? 's' : ''}</span>
                    <button
                      onClick={(e) => { e.stopPropagation(); navigate(`/horas/proyecto/${p.id}`); }}
                      style={{
                        ...navyBtn, padding: '4px 10px', fontSize: 11,
                        display: 'flex', alignItems: 'center', gap: 4,
                      }}
                    >
                      <Plus size={12} /> Nueva
                    </button>
                  </div>
                </div>

                {/* Sheets grouped by status — collapsible */}
                {!isProjectCollapsed && (
                  <div style={{ padding: '8px 0' }}>
                    {totalSheets === 0 ? (
                      <div style={{ padding: '12px 18px', fontSize: 12, color: '#94a3b8', fontStyle: 'italic' }}>
                        Sin tarjetas. Cree una nueva.
                      </div>
                    ) : STATUS_ORDER.filter(st => limitedSheets[st]?.length > 0).map(st => {
                      const cfg = STATUS_CONFIG[st] || STATUS_CONFIG.submitted;
                      const sheets = limitedSheets[st];
                      const fullCount = projectSheets[st]?.length || sheets.length;
                      const stKey = `${p.id}-${st}`;
                      const isStCollapsed = collapsedStatuses[stKey];
                      return (
                        <div key={st}>
                          {/* Status header — collapsible */}
                          <div
                            onClick={() => setCollapsedStatuses(prev => ({ ...prev, [stKey]: !prev[stKey] }))}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 6,
                              padding: '6px 18px', cursor: 'pointer', userSelect: 'none',
                            }}
                          >
                            {isStCollapsed
                              ? <ChevronRight size={12} style={{ color: '#94a3b8' }} />
                              : <ChevronDown size={12} style={{ color: '#94a3b8' }} />
                            }
                            <div style={{ width: 8, height: 8, borderRadius: '50%', background: cfg.color }} />
                            <span style={{ fontSize: 12, fontWeight: 600, color: NAVY }}>{cfg.label}</span>
                            <span style={{
                              fontSize: 10, padding: '0 6px', borderRadius: 8,
                              background: cfg.bg, color: cfg.color, fontWeight: 600,
                            }}>{fullCount}</span>
                          </div>

                          {/* Sheet rows */}
                          {!isStCollapsed && sheets.map(s => {
                            const isEditable = EDITABLE_STATUSES.has(s.status);
                            return (
                              <div
                                key={s.id}
                                onClick={() => navigate(isEditable ? `/horas/tarjeta/${s.id}` : `/horas/detalle/${s.id}`)}
                                style={{
                                  display: 'flex', alignItems: 'center', gap: 8,
                                  padding: '6px 18px 6px 44px', cursor: 'pointer', fontSize: 12,
                                  borderLeft: `3px solid ${cfg.color}`, marginLeft: 18,
                                  transition: 'background 0.1s',
                                }}
                                onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                              >
                                <span style={{ color: NAVY, fontWeight: 500, flex: 1 }}>
                                  {fmtDate(s.sheet_date)}
                                  <span style={{ color: '#94a3b8', marginLeft: 6 }}>
                                    Creado por: {s.supervisor_name} · {parseFloat(s.total_hours).toFixed(1)}h
                                  </span>
                                </span>
                                {/* Approval chain mini.
                                   V30.1: ganga roles can have multiple approvers, so the home
                                   pills label by ROLE (not actor name). Per-step actor names
                                   are surfaced on the TarjetaDetailPage approval tracker. */}
                                {(() => {
                                  const is3 = s.approval_type === '3_levels';
                                  const eng = 'Ingeniero(a)';
                                  const mgr = 'Gerente';
                                  const isRet = s.status === 'returned';
                                  const retTo = s.current_reviewer_role;
                                  const d = (n) => <span key={n} style={{ color: '#16a34a' }}>{n} ✓</span>;
                                  const c = (n) => <span key={n} style={{ color: '#d97706', fontWeight: 600 }}>{n}</span>;
                                  const r = (n) => <span key={n} style={{ color: '#ea580c', fontWeight: 600 }}>{n} ↩</span>;
                                  const p = (n) => <span key={n} style={{ color: '#94a3b8' }}>{n}</span>;
                                  const steps = [];
                                  // Supervisor (creator)
                                  if (isRet && retTo === 'supervisor') {
                                    steps.push(r('Supervisor(a)'));
                                  }
                                  // Engineer
                                  steps.push(<span key="sep0" style={{ color: '#cbd5e1' }}>{steps.length ? '→' : ''}</span>);
                                  if (isRet && retTo === 'engineer') steps.push(r(eng));
                                  else if (['pending_manager','pending_clerk','approved'].includes(s.status)) steps.push(d(eng));
                                  else if (s.status === 'submitted') steps.push(c(eng));
                                  else steps.push(p(eng));
                                  // Manager (3-level)
                                  if (is3) {
                                    steps.push(<span key="a1" style={{ color: '#cbd5e1' }}>→</span>);
                                    if (isRet && retTo === 'manager') steps.push(r(mgr));
                                    else if (['pending_clerk','approved'].includes(s.status)) steps.push(d(mgr));
                                    else if (['pending_manager','pending_engineer'].includes(s.status)) steps.push(c(mgr));
                                    else steps.push(p(mgr));
                                  }
                                  // Rev Final
                                  steps.push(<span key="a2" style={{ color: '#cbd5e1' }}>→</span>);
                                  if (s.status === 'approved') steps.push(d('Rev. Final'));
                                  else if (s.status === 'pending_clerk') steps.push(c('Rev. Final'));
                                  else steps.push(p('Rev. Final'));
                                  return <div style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 9 }}>{steps}</div>;
                                })()}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}

                    {/* "Ver todos" link — shows hidden count when project was truncated */}
                    <div
                      onClick={() => navigate(`/horas/proyecto/${p.id}`)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 4,
                        padding: '6px 18px', cursor: 'pointer', fontSize: 11,
                        color: AMBER_DARK, fontWeight: 600,
                      }}
                    >
                      <ChevronRight size={11} />
                      {hiddenCount > 0
                        ? `Ver todos los detalles (+${hiddenCount} más)`
                        : 'Ver todos los detalles'}
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {/* Global alerts */}
          {false && canReview && reviewSheets.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <h2 style={{ fontSize: 14, fontWeight: 700, color: NAVY, margin: '0 0 10px', display: 'flex', alignItems: 'center', gap: 6 }}>
                Bandeja de Revisiones
                {pendingCount > 0 && (
                  <span style={{
                    background: '#ca8a04', color: '#fff', borderRadius: 10,
                    padding: '0 7px', fontSize: 10, fontWeight: 700,
                  }}>{pendingCount}</span>
                )}
              </h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {reviewSheets.slice(0, 5).map(s => {
                  const cfg = STATUS_CONFIG[s.status] || STATUS_CONFIG.submitted;
                  return (
                    <div
                      key={s.id}
                      onClick={() => navigate(`/horas/detalle/${s.id}`)}
                      style={{
                        ...bubbleStyle, padding: '12px 16px', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: 12,
                        borderLeft: `3px solid ${cfg.color}`,
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: NAVY }}>
                          {s.project_code ? `${s.project_code} ` : ''}{s.project_name}
                        </span>
                        <span style={{ fontSize: 12, color: '#94a3b8', marginLeft: 8 }}>
                          {fmtDate(s.sheet_date)}
                        </span>
                      </div>
                      <div style={{
                        padding: '2px 8px', borderRadius: 8,
                        background: cfg.bg, color: cfg.color,
                        fontSize: 10, fontWeight: 600,
                      }}>{cfg.label}</div>
                    </div>
                  );
                })}
                {reviewSheets.length > 5 && (
                  <button onClick={() => setActiveTab('reviews')} style={{
                    background: 'none', border: 'none', color: AMBER_DARK,
                    fontSize: 12, cursor: 'pointer', padding: '4px 0',
                    fontFamily: 'Inter, sans-serif', fontWeight: 600,
                  }}>
                    Ver todas ({reviewSheets.length})...
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Alertas Globales */}
          {globalAlerts.length > 0 && (
            <div>
              <h2 style={{ fontSize: 14, fontWeight: 700, color: NAVY, margin: '0 0 10px', display: 'flex', alignItems: 'center', gap: 6 }}>
                <Bell size={14} /> Alertas
              </h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {globalAlerts.map(alert => (
                  <div key={alert.id}>
                    <div
                      onClick={() => setExpandedAlert(expandedAlert === alert.id ? null : alert.id)}
                      style={{
                        ...bubbleStyle, padding: '10px 14px', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: 8,
                        borderLeft: `3px solid ${alert.severity === 'error' ? '#ef4444' : alert.severity === 'warning' ? '#f59e0b' : '#3b82f6'}`,
                      }}
                    >
                      <AlertTriangle size={14} style={{
                        color: alert.severity === 'error' ? '#ef4444' : alert.severity === 'warning' ? '#f59e0b' : '#3b82f6',
                        flexShrink: 0,
                      }} />
                      <span style={{ flex: 1, fontSize: 12, color: NAVY }}>{alert.title}</span>
                    </div>
                    {expandedAlert === alert.id && (
                      <div style={{
                        ...bubbleStyle, padding: '14px 16px', marginTop: -1,
                        borderLeft: `3px solid ${alert.severity === 'error' ? '#ef4444' : alert.severity === 'warning' ? '#f59e0b' : '#3b82f6'}`,
                        borderTop: 'none', borderRadius: '0 0 16px 16px',
                      }}>
                        {alert.message && (
                          <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>{alert.message}</div>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); dismissAlert(alert.id); }}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 4,
                            background: 'none', border: `1px solid ${BORDER_COLOR}`,
                            borderRadius: 6, padding: '4px 10px', fontSize: 11,
                            color: '#64748b', cursor: 'pointer', fontFamily: 'Inter, sans-serif',
                          }}
                        >
                          <Check size={12} /> Marcar como vista
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Reviews tab removed — merged into Projects */}
      {false && activeTab === 'reviews' && (() => {
        if (reviewSheets.length === 0) return (
          <div style={{ ...bubbleStyle, padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>
            No hay tarjetas para revisar.
          </div>
        );

        // Group by project, then sort sheets oldest first within each
        const byProject = {};
        const sorted = [...reviewSheets].sort((a, b) => new Date(a.sheet_date) - new Date(b.sheet_date));
        for (const s of sorted) {
          const key = s.project_id;
          if (!byProject[key]) byProject[key] = { name: s.project_name, code: s.project_code, sheets: [] };
          byProject[key].sheets.push(s);
        }

        const STATUS_ORDER = ['submitted', 'returned', 'pending_engineer', 'pending_manager', 'pending_clerk'];

        return (
          <div>
            {Object.entries(byProject).map(([pid, proj]) => {
              // Group sheets by status within project
              const byStatus = {};
              for (const s of proj.sheets) {
                if (!byStatus[s.status]) byStatus[s.status] = [];
                byStatus[s.status].push(s);
              }

              return (
                <div key={pid} style={{ marginBottom: 20 }}>
                  {/* Project header */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <FolderOpen size={16} style={{ color: AMBER }} />
                    <span style={{ fontSize: 15, fontWeight: 700, color: NAVY }}>
                      {proj.code ? `${proj.code} ` : ''}{proj.name}
                    </span>
                    <span style={{ fontSize: 11, color: '#94a3b8' }}>({proj.sheets.length})</span>
                  </div>

                  {/* Status groups within project */}
                  {STATUS_ORDER.filter(st => byStatus[st]?.length > 0).map(st => {
                    const cfg = STATUS_CONFIG[st] || STATUS_CONFIG.submitted;
                    const groupSheets = byStatus[st];
                    return (
                      <div key={st} style={{ marginBottom: 10, marginLeft: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                          <div style={{ width: 8, height: 8, borderRadius: '50%', background: cfg.color }} />
                          <span style={{ fontSize: 12, fontWeight: 600, color: NAVY }}>{cfg.label}</span>
                          <span style={{ fontSize: 10, padding: '0 6px', borderRadius: 8, background: cfg.bg, color: cfg.color, fontWeight: 600 }}>{groupSheets.length}</span>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {groupSheets.map(s => {
                            const sCfg = STATUS_CONFIG[s.status] || STATUS_CONFIG.submitted;
                            const engName = s.engineer_name || 'Ingeniero(a)';
                            const mgrName = s.manager_name || 'Gerente';
                            const is3 = s.approval_type === '3_levels';
                            return (
                              <div
                                key={s.id}
                                onClick={() => navigate(s.status === 'returned' ? `/horas/detalle/${s.id}` : `/horas/detalle/${s.id}`)}
                                style={{
                                  ...bubbleStyle, padding: '12px 16px', cursor: 'pointer',
                                  display: 'flex', alignItems: 'center', gap: 12,
                                  borderLeft: `3px solid ${sCfg.color}`,
                                }}
                              >
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontSize: 13, fontWeight: 600, color: NAVY }}>{fmtDate(s.sheet_date)}</div>
                                  <div style={{ fontSize: 11, color: '#94a3b8' }}>
                                    Creado por: {s.supervisor_name} · {parseFloat(s.total_hours).toFixed(1)}h
                                  </div>
                                  {/* Approval chain */}
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 3, fontSize: 10 }}>
                                    {(() => {
                                      const steps = [];
                                      const done = (l) => <span key={l} style={{ color: '#16a34a', fontWeight: 600 }}>{l} ✓</span>;
                                      const cur = (l) => <span key={l} style={{ color: '#d97706', fontWeight: 700, padding: '1px 5px', background: '#fef3c7', borderRadius: 6 }}>{l}</span>;
                                      const pnd = (l) => <span key={l} style={{ color: '#94a3b8' }}>{l}</span>;
                                      const arr = (i) => <span key={`a${i}`} style={{ color: '#cbd5e1' }}>→</span>;
                                      if (['pending_manager','pending_clerk','approved'].includes(s.status)) steps.push(done(engName));
                                      else if (s.status === 'submitted') steps.push(cur(engName));
                                      else steps.push(pnd(engName));
                                      if (is3) {
                                        steps.push(arr(1));
                                        if (['pending_clerk','approved'].includes(s.status)) steps.push(done(mgrName));
                                        else if (['pending_manager','pending_engineer'].includes(s.status)) steps.push(cur(mgrName));
                                        else steps.push(pnd(mgrName));
                                      }
                                      steps.push(arr(2));
                                      if (s.status === 'approved') steps.push(done('Rev. Final'));
                                      else if (s.status === 'pending_clerk') steps.push(cur('Rev. Final'));
                                      else steps.push(pnd('Rev. Final'));
                                      return steps;
                                    })()}
                                  </div>
                                </div>
                                <div style={{ padding: '3px 10px', borderRadius: 10, background: sCfg.bg, color: sCfg.color, fontSize: 11, fontWeight: 600 }}>
                                  {sCfg.label}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* ═══ Summary Tab (Dashboard) ═══ */}
      {activeTab === 'summary' && (
        <div>
          {/* Date range filter */}
          <div className="date-range" style={{ display: 'flex', gap: 10, marginBottom: 14, alignItems: 'center', flexWrap: 'wrap' }}>
            <label style={{ fontSize: 12, color: '#64748b' }}>Desde:</label>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              style={{ ...fieldStyle, width: 'auto', padding: '5px 8px', fontSize: 12 }} />
            <label style={{ fontSize: 12, color: '#64748b' }}>Hasta:</label>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
              style={{ ...fieldStyle, width: 'auto', padding: '5px 8px', fontSize: 12 }} />
          </div>

          {!summary ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Cargando resumen...</div>
          ) : (
            <>
              {/* By Employee */}
              <div style={{ ...bubbleStyle, padding: 0, marginBottom: 14, overflow: 'auto' }}>
                <div style={{ padding: '10px 14px', fontWeight: 600, color: NAVY, fontSize: 13, borderBottom: `1px solid ${BORDER_COLOR}` }}>
                  Por Empleado
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: '#f8fafc' }}>
                      <th style={{ textAlign: 'left', padding: '8px 14px', fontWeight: 600, color: NAVY, borderBottom: `1px solid ${BORDER_COLOR}` }}>Empleado</th>
                      <th style={{ textAlign: 'right', padding: '8px 10px', fontWeight: 600, color: NAVY, borderBottom: `1px solid ${BORDER_COLOR}`, width: 70 }}>Reg</th>
                      <th style={{ textAlign: 'right', padding: '8px 10px', fontWeight: 600, color: NAVY, borderBottom: `1px solid ${BORDER_COLOR}`, width: 70 }}>Extra</th>
                      <th style={{ textAlign: 'right', padding: '8px 14px', fontWeight: 600, color: NAVY, borderBottom: `1px solid ${BORDER_COLOR}`, width: 70 }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.by_employee.map((r, i) => (
                      <tr key={i} style={{ borderBottom: `1px solid ${BORDER_COLOR}` }}>
                        <td style={{ padding: '8px 14px', color: NAVY, fontWeight: 500 }}>{r.name}</td>
                        <td style={{ textAlign: 'right', padding: '8px 10px', color: NAVY }}>{r.regular.toFixed(2)}</td>
                        <td style={{ textAlign: 'right', padding: '8px 10px', color: NAVY }}>{r.overtime.toFixed(2)}</td>
                        <td style={{ textAlign: 'right', padding: '8px 14px', color: NAVY, fontWeight: 600 }}>{r.total.toFixed(2)}</td>
                      </tr>
                    ))}
                    <tr style={{ background: '#f8fafc' }}>
                      <td style={{ padding: '8px 14px', fontWeight: 700, color: NAVY }}>TOTAL</td>
                      <td style={{ textAlign: 'right', padding: '8px 10px', fontWeight: 700, color: NAVY }}>{summary.totals.regular.toFixed(2)}</td>
                      <td style={{ textAlign: 'right', padding: '8px 10px', fontWeight: 700, color: NAVY }}>{summary.totals.overtime.toFixed(2)}</td>
                      <td style={{ textAlign: 'right', padding: '8px 14px', fontWeight: 800, color: AMBER_DARK }}>{summary.totals.total.toFixed(2)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* By Project */}
              <div style={{ ...bubbleStyle, padding: 0, marginBottom: 14, overflow: 'auto' }}>
                <div style={{ padding: '10px 14px', fontWeight: 600, color: NAVY, fontSize: 13, borderBottom: `1px solid ${BORDER_COLOR}` }}>
                  Por Proyecto
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: '#f8fafc' }}>
                      <th style={{ textAlign: 'left', padding: '8px 14px', fontWeight: 600, color: NAVY, borderBottom: `1px solid ${BORDER_COLOR}` }}>Proyecto</th>
                      <th style={{ textAlign: 'right', padding: '8px 10px', fontWeight: 600, color: NAVY, borderBottom: `1px solid ${BORDER_COLOR}`, width: 70 }}>Reg</th>
                      <th style={{ textAlign: 'right', padding: '8px 10px', fontWeight: 600, color: NAVY, borderBottom: `1px solid ${BORDER_COLOR}`, width: 70 }}>Extra</th>
                      <th style={{ textAlign: 'right', padding: '8px 14px', fontWeight: 600, color: NAVY, borderBottom: `1px solid ${BORDER_COLOR}`, width: 70 }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.by_project.map((r, i) => (
                      <tr key={i} style={{ borderBottom: `1px solid ${BORDER_COLOR}` }}>
                        <td style={{ padding: '8px 14px', color: NAVY, fontWeight: 500 }}>{r.name}</td>
                        <td style={{ textAlign: 'right', padding: '8px 10px', color: NAVY }}>{r.regular.toFixed(2)}</td>
                        <td style={{ textAlign: 'right', padding: '8px 10px', color: NAVY }}>{r.overtime.toFixed(2)}</td>
                        <td style={{ textAlign: 'right', padding: '8px 14px', color: NAVY, fontWeight: 600 }}>{r.total.toFixed(2)}</td>
                      </tr>
                    ))}
                    <tr style={{ background: '#f8fafc' }}>
                      <td style={{ padding: '8px 14px', fontWeight: 700, color: NAVY }}>TOTAL</td>
                      <td style={{ textAlign: 'right', padding: '8px 10px', fontWeight: 700, color: NAVY }}>{summary.totals.regular.toFixed(2)}</td>
                      <td style={{ textAlign: 'right', padding: '8px 10px', fontWeight: 700, color: NAVY }}>{summary.totals.overtime.toFixed(2)}</td>
                      <td style={{ textAlign: 'right', padding: '8px 14px', fontWeight: 800, color: AMBER_DARK }}>{summary.totals.total.toFixed(2)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Alerts */}
              <div style={{ ...bubbleStyle, padding: '12px 16px' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: NAVY, marginBottom: 8 }}>Alertas</div>
                {summary.alerts.pending_approval > 0 && (
                  <div style={{ fontSize: 12, color: '#ca8a04', display: 'flex', alignItems: 'center', gap: 5, marginBottom: 4 }}>
                    <Clock size={13} /> {summary.alerts.pending_approval} tarjeta{summary.alerts.pending_approval > 1 ? 's' : ''} pendiente{summary.alerts.pending_approval > 1 ? 's' : ''} de aprobación
                  </div>
                )}
                {summary.alerts.stale_drafts > 0 && (
                  <div style={{ fontSize: 12, color: '#ea580c', display: 'flex', alignItems: 'center', gap: 5, marginBottom: 4 }}>
                    <AlertTriangle size={13} /> {summary.alerts.stale_drafts} tarjeta{summary.alerts.stale_drafts > 1 ? 's' : ''} en borrador {'>'} 48h
                  </div>
                )}
                {summary.alerts.pending_approval === 0 && summary.alerts.stale_drafts === 0 && (
                  <div style={{ fontSize: 12, color: '#16a34a' }}>Sin alertas activas.</div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
